import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, todayISO, fmtRp } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FishConditionPicker } from "@/components/FishCondition";
import { GradeSelect } from "@/components/GradeSelect";

const empty = { species_id: "", size: "", origin_id: "", entry_date: todayISO(), quantity_mc: "", weight_per_mc_kg: 10, unit: "MC", location: "Cold Room 1", notes: "", expiry_date: "", fish_condition: "frozen", grade: "" };

export function InventoryForm({ open, onClose, onSaved, item, species, origins, onCorrectCost }) {
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const sp = species.find((s) => s.id === f.species_id);

  useEffect(() => {
    if (open) setF(item ? { ...empty, ...item, notes: item.notes || "", expiry_date: item.expiry_date || "", fish_condition: item.fish_condition || "frozen", grade: item.grade || "" } : empty);
  }, [open, item]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { species_id: f.species_id, size: f.size, origin_id: f.origin_id, entry_date: f.entry_date,
      quantity_mc: Number(f.quantity_mc), weight_per_mc_kg: f.unit === "KG" ? 1 : Number(f.weight_per_mc_kg), unit: f.unit || "MC", location: f.location, notes: f.notes, expiry_date: f.expiry_date || null, fish_condition: f.fish_condition, grade: f.grade || "" };
    try {
      if (item) await api.put(`/inventory/${item.id}`, payload);
      else await api.post("/inventory", payload);
      toast.success(item ? "Batch diperbarui" : "Batch inventaris ditambahkan");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="inventory-form-dialog">
        <DialogHeader><DialogTitle>{item ? "Edit Batch Inventaris" : "Tambah Batch Inventaris"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Jenis Ikan</Label>
              <Select value={f.species_id} onValueChange={(v) => { set("species_id", v); set("size", ""); }}>
                <SelectTrigger data-testid="select-inv-species"><SelectValue placeholder="Pilih jenis" /></SelectTrigger>
                <SelectContent>{species.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`opt-species-${s.id}`}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Size / Ukuran</Label>
              <Select value={f.size} onValueChange={(v) => set("size", v)} disabled={!sp}>
                <SelectTrigger data-testid="select-inv-size"><SelectValue placeholder="Pilih size" /></SelectTrigger>
                <SelectContent>{(sp?.sizes || []).map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Asal Ikan</Label>
            <Select value={f.origin_id} onValueChange={(v) => set("origin_id", v)}>
              <SelectTrigger data-testid="select-inv-origin"><SelectValue placeholder="Pilih asal / pelabuhan" /></SelectTrigger>
              <SelectContent>{origins.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Tanggal Masuk</Label><Input type="date" value={f.entry_date} onChange={(e) => set("entry_date", e.target.value)} required data-testid="input-inv-entry-date" /></div>
            <div className="space-y-1.5"><Label>Jumlah ({f.unit || "MC"})</Label><Input type="number" min="0" step={f.unit === "KG" ? "0.1" : "1"} value={f.quantity_mc} onChange={(e) => set("quantity_mc", e.target.value)} required data-testid="input-inv-quantity" /></div>
            <div className="space-y-1.5">
              <Label>Satuan Stok</Label>
              <Select value={f.unit || "MC"} onValueChange={(v) => v && set("unit", v)}>
                <SelectTrigger data-testid="select-inv-unit"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="MC">MC (master carton)</SelectItem><SelectItem value="KG">KG (per kilogram)</SelectItem></SelectContent>
              </Select>
            </div>
            {f.unit !== "KG" && <div className="space-y-1.5"><Label>Bobot per MC (kg)</Label><Input type="number" min="0.1" step="0.1" value={f.weight_per_mc_kg} onChange={(e) => set("weight_per_mc_kg", e.target.value)} required data-testid="input-inv-weight" /></div>}
            {item && (
              <div className="space-y-1.5"><Label>Harga Beli per {f.unit || "MC"}</Label>
                <div className="h-10 flex items-center justify-between px-3 rounded-md border border-slate-200 bg-slate-50 text-sm" data-testid="inv-form-cost">
                  <span className="font-mono text-slate-700">{item.unit_cost ? fmtRp(item.unit_cost) : "—"}</span>
                  {onCorrectCost && <button type="button" onClick={() => onCorrectCost(item)} className="text-xs font-semibold text-sky-700 hover:underline" data-testid="link-correct-cost">koreksi harga</button>}
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Lokasi Ruang</Label>
              <Select value={f.location} onValueChange={(v) => set("location", v)}>
                <SelectTrigger data-testid="select-inv-location"><SelectValue /></SelectTrigger>
                <SelectContent>{["Cold Room 1", "Cold Room 2", "Cold Room 3", "Blast Freezer"].map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <FishConditionPicker value={f.fish_condition} onChange={(v) => set("fish_condition", v)} testId="inv-fish-condition" />
            <GradeSelect value={f.grade} onChange={(v) => set("grade", v)} testId="select-inv-grade" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Tgl Kadaluarsa <span className="text-slate-400 font-normal">opsional</span></Label><Input type="date" value={f.expiry_date} onChange={(e) => set("expiry_date", e.target.value)} data-testid="input-inv-expiry" /><p className="text-[11px] text-slate-400">Kosongkan = otomatis {sp?.shelf_life_days || 180} hari dari tgl masuk</p></div>
            <div className="space-y-1.5"><Label>Catatan</Label><Input value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Opsional" data-testid="input-inv-notes" /></div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="btn-inv-cancel">Batal</Button>
            <Button type="submit" disabled={saving || !f.species_id || !f.size || !f.origin_id} className="bg-sky-600 hover:bg-sky-700" data-testid="btn-inv-submit">{saving ? "Menyimpan..." : "Simpan"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
